import React, { useState, useEffect } from 'react';
import mappingMemoryService, { StoredMapping } from '../../services/mappingMemoryService';

interface SavedMappingSelectorProps {
  importType: 'sales_data' | 'ad_spend_data';
  currentHeaders: string[];
  onSelectMapping: (mapping: Record<string, string>) => void;
  currentMapping: Record<string, string>;
}

const SavedMappingSelector: React.FC<SavedMappingSelectorProps> = ({
  importType,
  currentHeaders,
  onSelectMapping,
  currentMapping
}) => {
  const [savedMappings, setSavedMappings] = useState<StoredMapping[]>([]);
  const [selectedMappingId, setSelectedMappingId] = useState<string>('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newMappingName, setNewMappingName] = useState('');
  const [saveError, setSaveError] = useState('');

  // Load saved mappings on mount and when import type changes
  useEffect(() => {
    const mappings = mappingMemoryService.getSavedMappings(importType);
    setSavedMappings(mappings);
    
    // Try to find best matching mapping
    if (currentHeaders.length > 0 && Object.keys(currentMapping).length === 0) {
      const bestMatch = mappingMemoryService.findBestMatchingMapping(importType, currentHeaders);
      if (bestMatch) {
        setSelectedMappingId(bestMatch.id);
        onSelectMapping(bestMatch.mapping);
      }
    }
  }, [importType, currentHeaders, onSelectMapping, currentMapping]);

  // Handle selecting a saved mapping
  const handleSelectMapping = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const mappingId = e.target.value;
    setSelectedMappingId(mappingId);
    
    if (!mappingId) return;
    
    const mapping = savedMappings.find(m => m.id === mappingId);
    if (mapping) {
      onSelectMapping(mapping.mapping);
      
      // Update last used timestamp
      mappingMemoryService.updateMapping(mappingId, {});
    }
  };

  // Handle saving current mapping
  const handleSaveMapping = () => {
    if (!newMappingName.trim()) {
      setSaveError('Please enter a name for this mapping');
      return;
    }

    if (Object.keys(currentMapping).length === 0) {
      setSaveError('Cannot save an empty mapping');
      return;
    }

    try {
      const newMapping = mappingMemoryService.saveMapping(
        importType,
        newMappingName.trim(),
        currentMapping,
        currentHeaders
      );
      
      // Update local state
      setSavedMappings([newMapping, ...savedMappings]);
      setSelectedMappingId(newMapping.id);
      setShowSaveDialog(false);
      setNewMappingName('');
      setSaveError('');
    } catch (error) {
      setSaveError('Error saving mapping');
    }
  };

  // Handle deleting a mapping
  const handleDeleteMapping = () => {
    if (!selectedMappingId) return;
    
    const confirmed = window.confirm('Are you sure you want to delete this mapping?');
    if (!confirmed) return;
    
    const deleted = mappingMemoryService.deleteMapping(selectedMappingId);
    if (deleted) {
      setSavedMappings(savedMappings.filter(m => m.id !== selectedMappingId));
      setSelectedMappingId('');
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg border shadow-sm mb-6">
      <h3 className="text-lg font-medium mb-3">Column Mapping Templates</h3>
      
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex-grow">
          <select
            value={selectedMappingId}
            onChange={handleSelectMapping}
            className="w-full p-2 border rounded"
          >
            <option value="">-- Select a saved mapping --</option>
            {savedMappings.map(mapping => (
              <option key={mapping.id} value={mapping.id}>
                {mapping.name} ({new Date(mapping.lastUsed).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setShowSaveDialog(true)}
            className="px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded hover:bg-blue-100 transition-colors"
          >
            Save Current
          </button>
          
          {selectedMappingId && (
            <button
              onClick={handleDeleteMapping}
              className="px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded hover:bg-red-100 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>
      
      {showSaveDialog && (
        <div className="mt-3 p-3 bg-gray-50 border rounded">
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">
              Template Name
            </label>
            <input
              type="text"
              value={newMappingName}
              onChange={(e) => setNewMappingName(e.target.value)}
              placeholder="e.g., ROI Exteriors Sales Format"
              className="w-full p-2 border rounded"
            />
            {saveError && <p className="text-red-500 text-xs mt-1">{saveError}</p>}
          </div>
          
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowSaveDialog(false);
                setNewMappingName('');
                setSaveError('');
              }}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveMapping}
              className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Save Mapping
            </button>
          </div>
        </div>
      )}
      
      {savedMappings.length === 0 && !showSaveDialog && (
        <p className="text-gray-500 text-sm">
          No saved mappings yet. Configure your column mapping and save it to reuse in future imports.
        </p>
      )}
    </div>
  );
};

export default SavedMappingSelector;